import { useMemo, useState } from 'react';
import { T } from '@design/index';
import { patientApi } from '../api';
import {
  useProviderSlots,
  preferredTimeGrid,
  formatSlotTime,
  dateKeyFromOffset,
} from '../pages/care-services/useProviderSlots';

/**
 * Move an appointment to a different time.
 *
 * The web app could only cancel, so a patient who needed another slot had to
 * cancel and rebook — losing their place, and with it any seat the doctor had
 * published. `PATCH /appointments/:id/reschedule` has been there all along.
 *
 * Where the doctor publishes a schedule the real openings are offered and
 * booking one takes that seat. Where they publish none, the patient states a
 * preferred time and the screen says the clinic confirms it — the same
 * honesty the booking flow uses.
 */

const DAYS = 7;

export default function RescheduleAppointment({
  appointmentId,
  providerId,
  currentAt,
  onDone,
  onCancel,
}: {
  appointmentId: string;
  providerId: string | null;
  currentAt: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const slots = useProviderSlots(providerId ?? undefined, 14);
  const [dayOffset, setDayOffset] = useState(1);
  const [time, setTime] = useState<string | null>(null);
  const [slotId, setSlotId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const dayKey = dateKeyFromOffset(dayOffset);
  const open = slots.slotsFor(dayKey);

  const days = useMemo(
    () => Array.from({ length: DAYS }, (_, i) => {
      const key = dateKeyFromOffset(i);
      const d = new Date(`${key}T00:00:00`);
      return {
        offset: i,
        key,
        label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }),
      };
    }),
    [],
  );

  const submit = async () => {
    if (!time) { setError('Pick a new time first.'); return; }
    setBusy(true);
    setError('');
    try {
      await patientApi.rescheduleAppointment(appointmentId, {
        appointedAt: `${dayKey}T${time}:00`,
        ...(slotId ? { slotId } : {}),
      });
      onDone();
    } catch (e: any) {
      setError(e?.body?.errors?.[0]?.message || e?.body?.message || 'That time could not be booked. The appointment is unchanged.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}`, fontFamily: T.font,
    }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: T.text }}>Move this appointment</div>
      <div style={{ fontSize: 10.5, color: T.textSec, marginTop: 2 }}>
        Currently {new Date(currentAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })}
      </div>

      {/* Day strip */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginTop: 10, paddingBottom: 2 }}>
        {days.map((d) => (
          <button
            key={d.key}
            onClick={() => { setDayOffset(d.offset); setTime(null); setSlotId(null); }}
            style={{
              padding: '7px 12px', borderRadius: 10, cursor: 'pointer', whiteSpace: 'nowrap',
              fontSize: 11, fontWeight: 700, fontFamily: T.font,
              color: dayOffset === d.offset ? T.white : T.textSec,
              background: dayOffset === d.offset ? T.teal : T.white,
              border: `1px solid ${dayOffset === d.offset ? T.teal : T.border}`,
            }}
          >
            {d.label}
          </button>
        ))}
      </div>

      {slots.loading ? (
        <div style={{ fontSize: 11.5, color: T.textSec, marginTop: 10 }}>Checking availability…</div>
      ) : slots.hasSchedule ? (
        open.length === 0 ? (
          <div style={{
            fontSize: 11.5, color: T.textSec, marginTop: 10, background: T.ice,
            border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px', lineHeight: 1.5,
          }}>
            No openings left on this date.
            {slots.openDates.length > 0
              ? ` This doctor still has openings on ${slots.openDates.slice(0, 3).map((k) => new Date(`${k}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })).join(', ')}.`
              : ''}
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {open.map((s) => {
              const on = slotId === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => { setTime(s.startTime); setSlotId(s.id); }}
                  style={{
                    padding: '8px 12px', borderRadius: 10, cursor: 'pointer',
                    fontSize: 11.5, fontWeight: 700, fontFamily: T.font,
                    color: on ? T.white : T.navy,
                    background: on ? T.teal : T.white,
                    border: `1px solid ${on ? T.teal : `${T.navy}30`}`,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                  }}
                >
                  <span>{formatSlotTime(s.startTime)}</span>
                  <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.75 }}>
                    {s.remaining === 1 ? 'last seat' : `${s.remaining} seats`}
                  </span>
                </button>
              );
            })}
          </div>
        )
      ) : (
        <>
          <div style={{
            fontSize: 11, color: T.textSec, marginTop: 10, background: T.ice,
            border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 12px', lineHeight: 1.5,
          }}>
            This doctor does not publish a schedule here — pick a preferred time and the clinic confirms it.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {preferredTimeGrid().map((tm) => {
              const on = time === tm && !slotId;
              return (
                <button
                  key={tm}
                  onClick={() => { setTime(tm); setSlotId(null); }}
                  style={{
                    padding: '8px 12px', borderRadius: 10, cursor: 'pointer',
                    fontSize: 11.5, fontWeight: 700, fontFamily: T.font,
                    color: on ? T.white : T.navy,
                    background: on ? T.teal : T.white,
                    border: `1px solid ${on ? T.teal : `${T.navy}30`}`,
                  }}
                >
                  {formatSlotTime(tm)}
                </button>
              );
            })}
          </div>
        </>
      )}

      {error && (
        <div style={{ fontSize: 11.5, color: T.danger, marginTop: 10, lineHeight: 1.5 }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button
          onClick={submit}
          disabled={busy || !time}
          style={{
            padding: '9px 18px', borderRadius: 10, border: 'none',
            cursor: busy || !time ? 'default' : 'pointer', fontSize: 12, fontWeight: 700,
            color: T.white, background: T.teal, fontFamily: T.font,
            opacity: busy || !time ? 0.55 : 1,
          }}
        >
          {busy ? 'Moving…' : slotId ? 'Move to this slot' : 'Request this time'}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '9px 16px', borderRadius: 10, border: `1px solid ${T.border}`,
            background: T.white, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            color: T.textSec, fontFamily: T.font,
          }}
        >
          Keep the current time
        </button>
      </div>
    </div>
  );
}
